// frontend/shared-ui/src/components/Table.jsx
import React from "react";
import Spinner from "./Spinner";
import Checkbox from "./Checkbox";

export default function Table({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = "No records found",
  rowKey = "id",

  // --- Selection (optional) ---
  selectable = false,
  selectedIds = [],
  onSelectionChange, // (newSelectedIds: number[] | string[]) => void
  getId = (row) => row[rowKey],

  // --- Row click (optional) ---
  onRowClick,
  rowClassName,
}) {
  const allIds = data.map(getId);
  const selectedSet = new Set(selectedIds.map(String));
  const selectedCount = allIds.filter((id) =>
    selectedSet.has(String(id)),
  ).length;

  const allSelected = data.length > 0 && selectedCount === data.length;
  const someSelected = selectedCount > 0 && selectedCount < data.length;

  const toggleAll = (checked) => {
    if (!onSelectionChange) return;
    onSelectionChange(checked ? allIds : []);
  };

  const toggleOne = (id, checked) => {
    if (!onSelectionChange) return;
    const idStr = String(id);
    const next = new Set(selectedIds.map(String));
    if (checked) next.add(idStr);
    else next.delete(idStr);
    // Preserve original type by mapping back through `allIds`
    const map = new Map(allIds.map((x) => [String(x), x]));
    onSelectionChange([...next].map((s) => map.get(s) ?? s));
  };

  return (
    <div className="fh-table-wrap">
      <table className="fh-table">
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 40 }} className="fh-table__select-col">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(checked) => toggleAll(checked)}
                  ariaLabel="Select all rows"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="fh-table__loading"
              >
                <Spinner size="md" />
              </td>
            </tr>
          )}
          {!loading && data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="fh-table__empty"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
          {!loading &&
            data.map((row, idx) => {
              const id = getId(row);
              const isSelected = selectedSet.has(String(id));
              const extraClass = rowClassName ? rowClassName(row) : "";
              return (
                <tr
                  key={id ?? idx}
                  className={[isSelected ? "is-selected" : "", extraClass]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={
                    onRowClick
                      ? (e) => {
                          // don't fire row click when the user is selecting
                          if (
                            e.target.closest(".fh-checkbox-control") ||
                            e.target.closest("button") ||
                            e.target.closest("a")
                          )
                            return;
                          onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {selectable && (
                    <td
                      className="fh-table__select-col"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(checked) => toggleOne(id, checked)}
                        ariaLabel={`Select row ${idx + 1}`}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
